import { useContext, useState } from "react";
import { CSVLink } from "react-csv";
import {
  Alert,
  Group,
  Modal,
  Space,
  Button,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { collectionGroup, getDoc, getDocs, doc, getFirestore, query, where, Timestamp } from "firebase/firestore";
import { NotificationMSG, Service, Customer } from "../types";
import { GlobalDataContext } from "../contexts";
import { getAddressDetailsEnglish } from "../helpers/addressHelpers";
import { parseBirthFromCivilID } from "../utils";

const GenerateExecutionServicesCSVModal = (props: any) => {
  const dataContext = useContext(GlobalDataContext);
  const opened: boolean = props.opened;
  const setOpened = props.setOpened;
  const [confirming, setConfirming] = useState<boolean>(false);
  const [msg, setMsg] = useState<NotificationMSG | null>(null);
  const [downloadReady, setDownloadReady] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);

  const db = getFirestore();

  const handleGenerateCSV = async () => {
    setConfirming(true);
    setMsg(null);
    setDownloadReady(false);
    setData([]);

    try {
      // Query for services with waiting_for_execution tag and serviceType == "kfhtrade"
      const servicesQuery = query(
        collectionGroup(db, "services"),
        where("tags.serviceStatus.code", "==", "waiting_for_kyc"),
        where("serviceType", "==", "kfhtrade")
      );

      const servicesSnapshot = await getDocs(servicesQuery);
      const csvData: any[] = [];

      // Get customer data for each service
      const customerDataPromises = servicesSnapshot.docs.map(async (serviceDoc) => {
        const serviceData = serviceDoc.data() as Service;
        const customerID = serviceDoc.ref.parent.parent?.id;
        
        if (customerID) {
          const customerDocRef = doc(db, "customers", customerID);
          const customerDoc = await getDoc(customerDocRef);
          
          if (customerDoc.exists()) {
            const customerData = customerDoc.data() as Customer;
            return { serviceData, customerData };
          }
        }
        return null;
      });

      const customerResults = await Promise.all(customerDataPromises);

      // Map data to CSV format
      customerResults.forEach((result) => {
        if (result) {
          const { serviceData, customerData } = result;
          
          // The holder's own civil ID encodes the birth date; paciData is only a fallback, and never for
          // minors / representative accounts, whose paciData is the guardian's
          const civilBirth = parseBirthFromCivilID(customerData.civilID ?? "");
          const thirdPartyPaci = !!(serviceData.guardianCustomerID || serviceData.guardianPaciData);
          const birthDate = civilBirth
            ? `${String(civilBirth.day).padStart(2, '0')}${String(civilBirth.month).padStart(2, '0')}${civilBirth.year}`
            : !thirdPartyPaci && serviceData.paciData?.PersonalData?.BirthDate
            ? (() => {
                const date = new Date(serviceData.paciData.PersonalData.BirthDate);
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const year = date.getFullYear().toString();
                return `${day}${month}${year}`;
              })()
            : "?";

          // Get civil ID expiry date
          const civilIDExpiryDate = serviceData.paciData?.PersonalData?.CardExpiryDate
            ? (() => {
                const date = new Date(serviceData.paciData.PersonalData.CardExpiryDate);
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const year = date.getFullYear().toString();
                return `${year}${month}${day}`;  //yes it's flipped.  Don't ask me, ask the moron who defined the template.
              })()
            : "?";

          // Get address
          const address = getAddressDetailsEnglish(serviceData.paciData?.PersonalData?.Address || {}) || 
                         "?";

          csvData.push([
            (customerData.name_ar || "?").replace(/-/g, ''), // Long Name(O)
            (customerData.name_en || "?").replace(/-/g, ''), // Long Name€ no special character
            "INDV", // BP Category
            "CVID", // ID Type
            customerData.civilID || "?", // ID No
            "KW", // Country of Issue
            civilIDExpiryDate, // Expiry Date
            "Y", // Resident Flag
            address, // Street Address
            "KW", // Country
            "KFH Capital", // Contact Person
            "22987000", // Mobile No
            "Manager", // Designation
            "TRDR", // IC Trader
            "N", // Role
            908, // Default Bank ID
            "011440001029", // Bank A/C No
            "8", // Risk Level
            "1", // Networth
            "Y", // Actual Beneficiary
            "N", // Relationship with PEP
            "3489", // Source of Income
            birthDate, // Date of Birth
            "OTHR", // Occupation
            "KUWAIT", // Place of Birth
            "1", // Expected Investment
            "1", // Annual Income
            "AMATEUR", // Investment Expertise
            "", // Nature of Corporation
            "22987000", // Telephone No.1
            "OTHR", // EMPLOYMENT_STATUS
            "OTHR", // Employer Name
            "3489", // Purpose of A/C Opening
            "NONE", // Position of the PEP
            "NONE", // Name of the PEP
            "CRIC", // Investment Company ID Type
            "76471", // Investment Company ID No
            "2", // Sex
            "KW", // Citizen
            "KW", // Rounded Balance
            "KW", // Additional field
            customerData.gender === "F" ? "F" : "M", // Gender
            "N", // Another field
            "Y" // Final field
          ]);
        }
      });

      setData(csvData);
      setDownloadReady(true);
      setMsg({ type: "success", message: `CSV Generated Successfully with ${csvData.length} records` });
    } catch (error: any) {
      console.error("Error generating CSV:", error);
      setMsg({ type: "error", message: error.message });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        setMsg(null);
        setOpened(false);
        setConfirming(false);
        setDownloadReady(false);
        setData([]);
      }}
      title="Generate KCC CSV (KFH Trade Services - Waiting for Execution)"
      size="md"
    >
      <Space h="md" />
      
      <Group>
        <Button 
          color="blue" 
          onClick={handleGenerateCSV}
          loading={confirming}
        >
          Generate CSV
        </Button>
      </Group>

      <Space h="xl" />

      {msg && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color={msg.type === "success" ? "green" : "red"}
          title={msg.type === "success" ? "Success" : "Error"}
        >
          {msg.message}
          <br />
          {downloadReady && (
            <CSVLink 
              data={data} 
              filename={`kcc_kfhtrade_services_${new Date().toISOString().split('T')[0]}.csv`}
              style={{ marginTop: '10px', display: 'inline-block' }}
            >
              <Button size="sm" variant="outline" color="green">
                Download CSV
              </Button>
            </CSVLink>
          )}
        </Alert>
      )}
    </Modal>
  );
};

export default GenerateExecutionServicesCSVModal; 